/**
 * DasSection — remises DAS publiées par le gestionnaire (Batch #14.251).
 * Sélecteur mois + Fédéral / Provincial → montants (ou cases vides si rien n'est publié) + PDF.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, Landmark, Loader2 } from "lucide-react";
import { API, portalHeaders, SectionCard, inputCls, YEARS, MONTHS_LONG, fmtDate, portalDownload, err } from "./_shared";
import { fmtCAD as fmt } from "@/lib/format";

export default function DasSection({ primaryColor }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [authority, setAuthority] = useState("quebec");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/paie/das?year=${year}`, { headers: portalHeaders() });
      setData(r.data);
    } catch (e) { err(e, "Erreur chargement des remises"); setData({ items: [], fields: { federal: [], quebec: [] }, authorities: {} }); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const entry = (data?.items || []).find((it) => it.authority === authority && it.month === month) || null;
  // Batch #14.317 — vue client jumelée (une case RRQ, une case RQAP, une case AE), comme le formulaire de remise
  const fields = data?.client_fields?.[authority] || data?.fields?.[authority] || [];
  const amountOf = (fld) => (entry?.client_amounts ? entry.client_amounts[fld.code] : entry?.amounts?.[fld.code]);
  const published = new Set((data?.items || []).map((it) => `${it.authority}-${it.month}`));

  return (
    <SectionCard title="Remises DAS (déductions à la source)" icon={Landmark} primaryColor={primaryColor} testId="paie-portal-das">
      <p className="text-xs text-gray-500 -mt-2 mb-3">Sélectionnez le mois et le palier. Les montants apparaissent lorsque votre gestionnaire les a préparés.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Année</label>
          <select className={inputCls} value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="paie-portal-das-year">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Mois</label>
          <select className={inputCls} value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="paie-portal-das-month">
            {MONTHS_LONG.map((m, i) => <option key={m} value={i + 1}>{m}{published.has(`${authority}-${i + 1}`) ? " ●" : ""}</option>)}
          </select>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Palier</label>
          <div className="inline-flex w-full rounded-lg border overflow-hidden text-sm" data-testid="paie-portal-das-authority">
            {[["quebec", "Provincial"], ["federal", "Fédéral"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setAuthority(k)} className={`flex-1 px-3 py-2.5 font-medium transition ${authority === k ? "text-white" : "bg-white text-gray-600"}`}
                style={authority === k ? { backgroundColor: primaryColor } : {}} data-testid={`paie-portal-das-authority-${k}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      {!data ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-300 mx-auto" /></div> : (
        <div className="mt-4 border rounded-2xl overflow-hidden" data-testid={`paie-portal-das-card-${authority}-${month}`}>
          <div className="px-4 py-3 text-white flex items-center justify-between gap-2 flex-wrap" style={{ backgroundColor: primaryColor }}>
            <div>
              <div className="text-sm font-semibold">{data.authorities?.[authority]}</div>
              <div className="text-xs opacity-80">{MONTHS_LONG[month - 1]} {year}</div>
            </div>
            {entry ? (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/20" data-testid="paie-portal-das-published">Préparé le {String(entry.published_at || "").slice(0, 10)}</span>
            ) : (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/20" data-testid="paie-portal-das-empty">En attente de votre gestionnaire</span>
            )}
          </div>
          <div className="divide-y">
            {fields.map((fld) => (
              <div key={fld.code} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" data-testid={`paie-portal-das-field-${fld.code}`}>
                <span className="text-gray-700">{fld.label}</span>
                <span className={`font-mono min-w-[110px] text-right px-2 py-1 rounded-lg border ${entry ? "bg-white text-gray-900" : "bg-gray-50 text-transparent border-dashed"}`}>{entry ? fmt(amountOf(fld)) : "0,00 $"}</span>
              </div>
            ))}
            <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm font-bold bg-gray-50" data-testid="paie-portal-das-total">
              <span>Total à remettre</span>
              <span className={`font-mono min-w-[110px] text-right px-2 py-1 rounded-lg border ${entry ? "bg-white" : "text-transparent border-dashed"}`}>{entry ? fmt(entry.total) : "0,00 $"}</span>
            </div>
            {entry && authority === "federal" && (entry.gross_payroll != null || entry.employee_count != null) && (
              <div className="px-4 py-2.5 text-sm space-y-1 bg-slate-50" data-testid="paie-portal-das-pd7a">
                <div className="flex items-center justify-between gap-3"><span className="text-gray-700">Rémunération fédérale de la période — T4 case 14 (PD7A)</span><span className="font-mono text-gray-900">{fmt(entry.gross_payroll || 0)}</span></div>
                <div className="flex items-center justify-between gap-3"><span className="text-gray-700">Nombre d'employés payés dans la période</span><span className="font-mono text-gray-900">{entry.employee_count ?? 0}</span></div>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <span className="text-gray-700">Échéance du versement</span>
              <span className={`font-medium ${entry?.due_date ? "text-gray-900" : "text-gray-300"}`} data-testid="paie-portal-das-due">{entry?.due_date ? fmtDate(entry.due_date) : "—"}</span>
            </div>
            {entry?.note && <div className="px-4 py-2.5 text-xs text-gray-700 bg-amber-50" data-testid="paie-portal-das-note">{entry.note}</div>}
          </div>
          {entry && (
            <div className="p-3 border-t bg-white">
              <button type="button" disabled={busy} onClick={() => portalDownload(`/portal/paie/das/${authority}/${year}/${month}/pdf`, `DAS_${authority}_${year}-${String(month).padStart(2, "0")}.pdf`, setBusy)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: primaryColor }} data-testid="paie-portal-das-pdf">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Télécharger en PDF
              </button>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}
